import { useGetProfile } from "../generated/hook";
import { useConnectedUser } from "../stores/useConnectedUser";
import { useEffect } from "react";

export const useAuth = () => {
  const { firstName, setFirstName, setLastName, clear } = useConnectedUser();

  // This will automatically handle token verification and user data fetching
  const { data, isLoading, error, refetch } = useGetProfile({
    query: {
      retry: (failureCount, error: any) => {
        // Stop retrying if we get a 401 or 403 error
        if (
          error?.response?.status === 401 ||
          error?.data?.status === 401 ||
          error?.response?.status === 403 ||
          error?.data?.status === 403
        ) {
          return false;
        }
        // Retry up to 3 times with exponential backoff
        return failureCount < 3;
      },
      refetchInterval: 120000, // 2 minutes in milliseconds
      refetchIntervalInBackground: false, // Don't refetch when window is not focused
      refetchOnWindowFocus: false, // Don't refetch when window regains focus
      enabled: !!firstName, // Only enable if we have a user
    },
  });

  // Update the store when user data changes
  useEffect(() => {
    if (error) {
      clear();
    } else if (data) {
      // Extract first and last name from displayName if available
      const names = data.displayName?.split(" ") || [];
      setFirstName(names[0] || null);
      setLastName(names.slice(1).join(" ") || null);
    }
  }, [data, error, clear, setFirstName, setLastName]);

  // User is considered authenticated if we have user data and no error
  const isAuthenticated = !!data && !error;

  return {
    user: data
      ? {
          id: data.id,
          email: data.email,
          displayName: data.displayName,
          isUserFrom: data.isUserFrom || [],
          preferredViewPeriod: data.preferredViewPeriod || "week",
        }
      : null,
    loading: isLoading,
    isAuthenticated,
    refetch, // Expose refetch function for manual refresh
  };
};

export default useAuth;
